<template>
  <div
    v-if="banners.length"
    class="promo-banner critical-component-hide"
    :style="`
      --promo-bg-colour: ${liquid.bgColour};
      --promo-text-colour: ${liquid.textColour};
    `"
  >
    <div class="promo-banner__container container">
      <span
        v-if="banners.length"
        id="promo-banner-title"
        class="visually-hidden"
        v-text="$string('general.site_header.promo_banner')"
      />

      <splide
        v-if="banners.length"
        class="promo-banner__list"
        :options="splideConfig"
      >
        <splide-slide
          v-for="block of banners"
          :key="block.id"
          class="promo-banner__list-item text-other-label-small"
        >
          <span v-html="getBlockText(block.title)" />
        </splide-slide>
      </splide>

      <a
        v-if="liquid.storeLocator"
        :href="liquid.storeLocator"
        class="promo-banner__store-locator"
      >
        <icon-location />

        <span
          class="text-other-label-small"
          v-text="$string('general.site_header.location')"
        />
      </a>

      <multi-store-options-inline
        :language-selector="false"
        namespace="header"
      />
    </div>
  </div>
</template>

<script>
/**
 * Vue: Promo banner (promo-banner)
 * -----------------------------------------------------------------------------
 * Promo banner template.
 * - Conditionally loaded when section has blocks using Liquid.
 * - Scroll-snap is used for basic scrolling, you will need to add the carousel
 *   functionality if this is required.
 *
 * @param {Object} autoscroll - Autoscroll settings.
 * @param {Array} blocks - Promo blocks.
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 *
 */
import { Splide, SplideSlide } from '@splidejs/vue-splide'

import IconLocation from '~icons/general/location.svg'

import MultiStoreOptionsInline from '~async/multi-store-options/multi-store-options-inline'
import { mapState } from 'vuex'

export default {
  name: 'PromoBanner',

  components: {
    IconLocation,
    MultiStoreOptionsInline,
    Splide,
    SplideSlide,
  },

  props: {
    autoscroll: {
      type: Object,
      default: () => ({
        enable: false,
        timing: 5,
      }),
    },
    blocks: {
      type: Array,
      default: () => [],
    },
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        storeLocator: '',
        bgColour: '',
        textColour: '',
      }),
    },
    geoConfig: {
      type: Object,
      default: () => ({
        geoOnly: true,
        fallback: true,
      }),
    },
  },

  data() {
    return {
      splideConfig: {
        arrows: false,
        autoWidth: true,
        autoplay: this.autoscroll.enable,
        drag: false,
        interval: this.autoscroll.timing * 1000,
        pagination: false,
        speed: 1500,
        type: 'loop',
      },
    }
  },

  computed: {
    /**
     * Map Vuex state.
     */
    ...mapState({
      geolocation: (state) => state.geolocation,
    }),

    /**
     * Compute banners that include the user's geolocation or, if mixed content
     * is allowed, show banners that do not have targets selected
     * @returns {array}
     */
    banners() {
      const useMixedContent = !this.geoConfig.geoOnly
      return this.blocks.filter((block) => {
        if (block.zipcodes) {
          return block.zipcodes.split(',').includes(this.geolocation.postalCode)
        }
        return useMixedContent && !block.zipcodes
      })
    },
  },

  watch: {
    banners(after, before) {
      if (before.length === after.length) {
        return
      }
      this.updateSiteHeaderHeight()
    },
  },

  mounted() {
    this.updateSiteHeaderHeight()
  },

  methods: {
    /**
     * Get block text.
     * - Applies text class to <a> elements.
     * @param {String} block - Block text.
     * @returns {String}
     */
    getBlockText(block) {
      return block
        .replaceAll('<a', '<a class="text-other-label-small"')
        .replaceAll('<strong', '<strong class="text-other-label-small"')
    },

    async updateSiteHeaderHeight() {
      await this.$nextTick()
      cnvs.EventBus.emit('cnvs:update-site-header-height')
    },
  },
}
</script>

<style lang="scss">
@import './promo-banner';
</style>
